import React, { useState } from 'react'
import {
  Arrow,
  ArrowContainerSearch,
  ArrowDivider,
  ContainerResearch,
  LabelSelected,
  MenuSearchContainer,
  MenuSearchItem
} from './styles'
import { Input } from '../Input'

export interface IField {
  value: string | number
  label: string
  inputMask?: string
  inputType?: string
}

export interface IChangeManualField {
  change: boolean
  value?: string | number
}

/**
 * This component will render a input with select, extends HTMLInputElement props
 * @param {array} fields array of fields to display in select component
 * @param {Function} selectedValueCallback function callback to return value from selected item
 * @param {object} changeManualField object to manual change the selected item
 * @param {string} message prop needed to pass to Input component, display message error
 * @param {boolean} error prop needed to pass to Input component, boolean to display error
 * @param {string | number} defaultSelect set an initial default value, value needed to be in IField array
 */
interface InputWithSelectProps extends React.InputHTMLAttributes<HTMLInputElement> {
  fields: IField[]
  selectedValueCallback: (selected: string | number) => void
  changeManualField?: IChangeManualField
  message?: string
  error?: boolean
  defaultSelect?: string | number
}

export const InputWithSelect = React.forwardRef(
  (
    { fields, selectedValueCallback, changeManualField, message, error, defaultSelect, ...rest }: InputWithSelectProps,
    ref: React.ForwardedRef<HTMLInputElement>
  ): JSX.Element => {
    const [selectLabel, setSelectLabel] = useState<string>(fields[0].label)
    const [openMenuState, setOpenMenuState] = useState(false)
    const [selectFieldState, setSelectFieldState] = useState<IField>(fields[0])

    const handleSelectedValue = React.useCallback(
      (field: IField, id: number) => {
        setOpenMenuState(false)
        setSelectLabel(field.label)
        selectedValueCallback(field.value)
        setSelectFieldState(fields[id])
      },
      [fields, selectedValueCallback]
    )

    React.useEffect(() => {
      if (defaultSelect) {
        fields.forEach((field, id) => {
          if (field.value === defaultSelect) {
            handleSelectedValue(field, id)
          }
        })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    React.useEffect(() => {
      if (changeManualField?.change) {
        fields.forEach((field, id) => {
          if (field.value === changeManualField.value) {
            handleSelectedValue(field, id)
          }
        })
      }
    }, [changeManualField, fields, handleSelectedValue])

    return (
      <ContainerResearch>
        <LabelSelected onClick={() => setOpenMenuState(!openMenuState)}>
          {selectLabel}
          <MenuSearchContainer openMenuState={openMenuState}>
            {fields.map((field, id) => {
              if (field.label !== selectLabel) {
                return (
                  <MenuSearchItem
                    data-testid="input-with-select-select-item"
                    key={id}
                    onClick={() => {
                      handleSelectedValue(field, id)
                    }}
                  >
                    {field.label}
                  </MenuSearchItem>
                )
              }
            })}
          </MenuSearchContainer>
          <ArrowContainerSearch>
            <ArrowDivider>
              <Arrow openMenuState={openMenuState}></Arrow>
            </ArrowDivider>
          </ArrowContainerSearch>
        </LabelSelected>
        <Input
          {...rest}
          ref={ref}
          data-testid="input-with-select-input"
          message={message}
          error={error}
          type={rest.type ?? selectFieldState.inputType ?? 'text'}
          mask={selectFieldState.inputMask}
          onMouseOver={() => setOpenMenuState(false)}
        />
      </ContainerResearch>
    )
  }
)
InputWithSelect.displayName = 'InputWithSelect'
